import { TGetRegisterSupportOrganizationTypeDto } from "@/api/services/admin/rso/dtos/get-rso-list.dto";
import { IconChevronRight } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { Linkable } from "@/components/elements/Linkable";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT, DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { RsoTableSorting } from "@/features/rso/components/RsoTableSorting";
import { RSO_LIST_TAB } from "@/features/rso/constants/rsoListTab";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { ERSOContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

type TRsoTableProps = {
  contractStatuses: ERSOContractStatus[];
  data: TGetRegisterSupportOrganizationTypeDto[];
  isLoading: boolean;
};

export const RsoTable: FC<TRsoTableProps> = ({ contractStatuses, data, isLoading }) => {
  const navigate = useNavigate();
  const columns = useMemo<MRT_ColumnDef<TGetRegisterSupportOrganizationTypeDto>[]>(
    () => [
      {
        header: "機関名",
        id: "rsoName",
        accessorKey: "rsoName",
        Cell: ({ row }) => truncateWithEllipsis(row.original.rsoName, 20),
        Header: <RsoTableSorting name="rso.rsoName">機関名</RsoTableSorting>,
      },
      {
        header: "登録番号",
        id: "rsoRegistrationNumber",
        accessorKey: "rsoRegistrationNumber",
        Header: <RsoTableSorting name="rso.rsoRegistrationNumber">登録番号</RsoTableSorting>,
        size: 100,
      },
      {
        header: "所属機関数",
        Cell: ({ row }) => row.original.companyCount || 0,
        size: 20,
      },
      {
        header: "課金対象数",
        size: 120,
        Cell: ({ row }) => {
          return row.original.currentMonthMaxBillableEmployeeCount ?? EMPTY_STR.TEXT;
        },
      },
      {
        header: "前月課金数",
        size: 120,
        Cell: ({ row }) => {
          return row.original.previousMonthMaxBillableEmployeeCount ?? EMPTY_STR.TEXT;
        },
      },
      {
        header: "申請数",
        id: "applicationCount",
        size: 60,
        Cell: ({ row }) => row.original.applicationCount ?? 0,
      },
      {
        header: "contractStatus",
        Header: () => {
          if (contractStatuses.includes(ERSOContractStatus.DURING_CONTRACT))
            return <RsoTableSorting name="rso.contractStartDate">契約開始日</RsoTableSorting>;
          if (contractStatuses.includes(ERSOContractStatus.CANCELED_CONTRACT))
            return <RsoTableSorting name="rso.contractEndDate">解約日</RsoTableSorting>;
          if (contractStatuses.includes(ERSOContractStatus.TRIAL))
            return <RsoTableSorting name="rso.trialStartDate">トライアル開始日</RsoTableSorting>;
          if (contractStatuses.includes(ERSOContractStatus.TRIAL_CANCELLED))
            return <RsoTableSorting name="rso.trialEndDate">トライアル終了日</RsoTableSorting>;
        },
        Cell: ({ row }) => {
          if (contractStatuses.includes(ERSOContractStatus.DURING_CONTRACT))
            return row.original.contractStartDate ? dayjs(row.original.contractStartDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ERSOContractStatus.CANCELED_CONTRACT))
            return row.original.contractEndDate ? dayjs(row.original.contractEndDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ERSOContractStatus.TRIAL))
            return row.original.trialStartDate ? dayjs(row.original.trialStartDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ERSOContractStatus.TRIAL_CANCELLED))
            return row.original.trialEndDate ? dayjs(row.original.trialEndDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        size: 120,
      },
      {
        header: "最終ログイン日時",
        id: "lastLoginAt",
        size: 150,
        Cell: ({ row }) => (row.original.lastLoginAt ? dayjs(row.original.lastLoginAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE),
      },
      {
        header: "詳細",
        id: "action",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Linkable href={getDynamicRoute(AppRoutes.rsoDetail, { id })}>
              <IconBox>
                <IconChevronRight />
              </IconBox>
            </Linkable>
          );
        },
        size: 20,
      },
    ],
    [contractStatuses],
  );

  const { table } = useTable({
    columns,
    rows: data,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.rsoDetail, { id: row.id })),
    pinningColumns: ["action"],
    muiTableBodyRowProps: ({ row }) => ({
      className: row.original.deletedAt ? "deleted" : "",
    }),
  });

  return (
    <StyledTable>
      <Table table={table} isLoading={isLoading} maxHeight="calc(100vh - 420px)" />
    </StyledTable>
  );
};

const StyledTable = styled(Box)`
  .deleted {
    opacity: 0.5;
  }
`;

// Sort fields shared by every tab (機関名, 登録番号).
export const RSO_COMMON_SORT_FIELDS = ["rso.rsoName", "rso.rsoRegistrationNumber"];

// Date sort field that only applies to its own tab (the date column shown changes per tab).
export const RSO_TAB_DATE_SORT_FIELD = {
  [RSO_LIST_TAB.DURING_CONTRACT]: "rso.contractStartDate",
  [RSO_LIST_TAB.CANCELED_CONTRACT]: "rso.contractEndDate",
  [RSO_LIST_TAB.TRIAL]: "rso.trialStartDate",
  [RSO_LIST_TAB.TRIAL_CANCELLED]: "rso.trialEndDate",
};
